import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

const TUTOR_PER_PAGE = 6;

export function useTutorSearch(searchThunk) {
    const dispatch = useDispatch();
    const abortRef = useRef(null);

    const [tutors, setTutors] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleSearch = useCallback(
        (nextPage = 0, formValues = {}) => {
            if (abortRef.current) {
                abortRef.current.abort();
            }

            const controller = new AbortController();
            abortRef.current = controller;

            setPage(nextPage);
            setIsLoading(true);
            setError(null);

            return dispatch(
                searchThunk({
                    signal: controller.signal,
                    skip: nextPage,
                    limit: TUTOR_PER_PAGE,
                    ...formValues,
                })
            )
                .unwrap()
                .then((result) => {
                    if (controller.signal.aborted) return result;

                    setTotalPages(result.pages ?? 0);
                    setTutors(result.list ?? []);
                    return result;
                })
                .catch((err) => {
                    if (err?.name === "AbortError" || controller.signal.aborted) return;

                    setError(err);
                })
                .finally(() => {
                    if (!controller.signal.aborted) {
                        setIsLoading(false);
                    }
                });
        },
        [dispatch, searchThunk]
    );

    useEffect(() => {
        handleSearch(0, {});

        return () => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, [handleSearch]);

    return {
        tutors,
        page,
        totalPages,
        isLoading,
        error,
        tutorPerPage: TUTOR_PER_PAGE,
        handleSearch,
    };
}
